'use client';

import { useCallback, useMemo } from 'react';
import { useFormContext, useWatch } from 'react-hook-form';

import type { DeliveryMethodType, ProductApiResponse, TailoredApiData, VariantKey } from '@libs/common-types';

import {
    findSkuForVariantChange,
    isProductAvailableForAutoship,
    isVariantInStock,
    mapVariantGroups,
    type VariantGroup,
    type VariantGroupOption,
} from '../../product-details';
import type { MiniPdpForm } from '../types';

type MiniPdpBaseProduct = ProductApiResponse['data'];

export type MiniPdpVariant = VariantGroupOption;
export type MiniPdpVariantGroup = VariantGroup;

export type MiniPdpVariantsProps = Readonly<{
    base: MiniPdpBaseProduct | null;
    tailored: TailoredApiData | null;
}>;

export const useMiniPdpVariants = ({ base, tailored }: MiniPdpVariantsProps) => {
    const { getValues, setValue } = useFormContext<MiniPdpForm>();
    const selectedSku = useWatch<MiniPdpForm, 'selectedSku'>({ name: 'selectedSku' });
    const fulfilmentMethod = useWatch<MiniPdpForm, 'fulfilmentMethod'>({ name: 'fulfilmentMethod' });

    const activeSku = base?.variants[selectedSku] ? selectedSku : base?.defaultSku;
    const activeProduct = activeSku && base ? base.variants[activeSku] : undefined;
    const activeVariants = activeProduct?.variantValues ?? {};
    const method = fulfilmentMethod.toUpperCase() as DeliveryMethodType;

    const handleVariantChange = useCallback(
        (group: VariantKey, value: string) => {
            if (!base?.variantToSkuMap) return;

            const sku = findSkuForVariantChange({
                activeVariants,
                group,
                isSkuAvailable: candidateSku =>
                    isVariantInStock({
                        sku: candidateSku,
                        product: base.variants[candidateSku],
                        method,
                        tailored: tailored ?? undefined,
                    }),
                value,
                variantToSkuMap: base.variantToSkuMap,
            });

            if (!sku) return;

            setValue('selectedSku', sku, { shouldDirty: true, shouldValidate: true });

            const nextProduct = base.variants[sku];
            const isAutoshipUnavailable = !nextProduct || !isProductAvailableForAutoship(nextProduct);

            if (getValues('purchaseOption') === 'autoship' && isAutoshipUnavailable) {
                setValue('purchaseOption', 'one-time');
            }
        },
        [activeVariants, base, method, setValue, getValues, tailored]
    );

    const variantGroups = useMemo(
        () =>
            mapVariantGroups({
                activeVariants,
                method,
                tailored,
                uniqueVariantValues: base?.uniqueVariantValues,
                variantToSkuMap: base?.variantToSkuMap,
                variants: base?.variants ?? {},
            }),
        [activeVariants, base, method, tailored]
    );

    return {
        activeProduct,
        activeSku,
        handleVariantChange,
        variantGroups,
    };
};
